import { useState } from 'react'
import { Modal } from '@/components/Modal'
import { SearchableSelect } from '@/components/SearchableSelect'
import { optionsFrom, useIndustries, useWorkLevels } from './queries'
import { joinMonthValue, splitMonthValue, type WorkExperienceRow } from './types'

/**
 * Port of Views/Profile/Information/_WorkExperience.cshtml + _workExperience.js.
 *
 * Optional as a whole: a fresh graduate submits with none, and SaveAll then
 * posts a single all-blank row rather than an empty array (see types.ts).
 * Nothing here is marked required for that reason — the `required` attributes in
 * the Razor sat inside a form that was never submitted natively, so they never
 * fired.
 */
interface Props {
  rows: WorkExperienceRow[]
  onChange: (rows: WorkExperienceRow[]) => void
  /** The "Add" button lives in the section header, which the page owns. */
  isModalOpen: boolean
  onCloseModal: () => void
}

const EMPTY: WorkExperienceRow = {
  Company: '',
  mstIndustryID: '',
  IndustryName: '',
  Position: '',
  mstWorkExperienceLevelID: '',
  LevelName: '',
  MonthIn: '',
  YearIn: '',
  MonthOut: '',
  YearOut: '',
  isUntilNow: false,
  Salary: '',
  JobDescription: '',
}

export function WorkExperienceSection({
  rows,
  onChange,
  isModalOpen,
  onCloseModal,
}: Props): JSX.Element {
  const [draft, setDraft] = useState<WorkExperienceRow>(EMPTY)
  const [error, setError] = useState('')

  const industries = useIndustries()
  const levels = useWorkLevels()

  function add(): void {
    if (!draft.Company.trim()) return setError('Please enter the company name.')
    if (!draft.Position.trim()) return setError('Please enter your position.')
    if (!draft.YearIn) return setError('Please choose the start period.')
    if (!draft.isUntilNow && !draft.YearOut) return setError('Please choose the end period.')

    setError('')
    onChange([...rows, draft])
    setDraft(EMPTY)
    onCloseModal()
  }

  return (
    <>
      <div className="table-responsive">
        <table className="table table-hover" id="tblWorkExperience">
          <thead>
            <tr>
              <th className="text-center">#</th>
              <th className="text-center">Company</th>
              <th className="text-center">Position</th>
              <th className="text-center">Level</th>
              <th className="text-center">Year In</th>
              <th className="text-center">Year Out</th>
              <th className="text-center" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-muted">
                  No work experience added yet
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={`${row.Company}-${row.YearIn}-${index}`}>
                  <td className="text-center">{index + 1}</td>
                  <td>{row.Company}</td>
                  <td>{row.Position}</td>
                  <td>{row.LevelName}</td>
                  <td className="text-center">{row.YearIn}</td>
                  <td className="text-center">{row.isUntilNow ? 'Until Now' : row.YearOut}</td>
                  <td className="text-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger"
                      onClick={() => onChange(rows.filter((_, i) => i !== index))}
                    >
                      <i className="ti ti-trash" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        id="workExperienceModal"
        title="Add Work Experience"
        isOpen={isModalOpen}
        onClose={onCloseModal}
      >
      <form className="needs-validation" id="addWorkExperienceInfo" noValidate>
        <div id="formWorkExperience">
          <div className="mb-3">
            <label className="form-label">Company </label>
            <input
              type="text"
              className="form-control"
              id="txtCompany"
              placeholder="Company Name"
              value={draft.Company}
              onChange={(e) => setDraft({ ...draft, Company: e.target.value })}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Industry </label>
            <SearchableSelect
              id="slsIndustry"
              options={optionsFrom(
                industries.data?.Data,
                (i) => i.mstIndustryID,
                (i) => i.IndustryName,
              )}
              value={draft.mstIndustryID}
              onChange={(id) =>
                setDraft({
                  ...draft,
                  mstIndustryID: id,
                  IndustryName:
                    industries.data?.Data?.find((i) => String(i.mstIndustryID) === id)
                      ?.IndustryName ?? '',
                })
              }
              placeholder="Select industry"
              loading={industries.isLoading}
              emptyLabel={null}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Position </label>
            <input
              type="text"
              className="form-control"
              id="txtPosition"
              placeholder="Position Name"
              value={draft.Position}
              onChange={(e) => setDraft({ ...draft, Position: e.target.value })}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Level </label>
            <SearchableSelect
              id="slsLevel"
              options={optionsFrom(
                levels.data?.Data,
                (l) => l.mstWorkExperienceLevelID,
                (l) => l.WorkExperienceLevelName,
              )}
              value={draft.mstWorkExperienceLevelID}
              onChange={(id) =>
                setDraft({
                  ...draft,
                  mstWorkExperienceLevelID: id,
                  LevelName:
                    levels.data?.Data?.find((l) => String(l.mstWorkExperienceLevelID) === id)
                      ?.WorkExperienceLevelName ?? '',
                })
              }
              placeholder="Select level"
              loading={levels.isLoading}
              emptyLabel={null}
            />
          </div>

          <div className="row">
            <div className="mb-3 col-md-6">
              <label className="form-label">Date In </label>
              <input
                type="month"
                className="form-control"
                id="dpWorkExperienceStart"
                value={joinMonthValue(
                  draft.YearIn ? Number(draft.YearIn) : null,
                  draft.MonthIn ? Number(draft.MonthIn) : null,
                )}
                onChange={(e) => {
                  const { month, year } = splitMonthValue(e.target.value)
                  setDraft({
                    ...draft,
                    MonthIn: month ? String(month) : '',
                    YearIn: year ? String(year) : '',
                  })
                }}
              />
            </div>
            <div className="mb-3 col-md-6">
              <label className="form-label">Date Out </label>
              <input
                type="month"
                className="form-control"
                id="dpWorkExperienceEnd"
                disabled={draft.isUntilNow}
                value={joinMonthValue(
                  draft.YearOut ? Number(draft.YearOut) : null,
                  draft.MonthOut ? Number(draft.MonthOut) : null,
                )}
                onChange={(e) => {
                  const { month, year } = splitMonthValue(e.target.value)
                  setDraft({
                    ...draft,
                    MonthOut: month ? String(month) : '',
                    YearOut: year ? String(year) : '',
                  })
                }}
              />
            </div>
          </div>

          <div className="row">
            <div className="mb-3 col-md-6" />
            <div className="mb-3 col-md-6">
              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="isWorkExperienceUntilNow"
                  checked={draft.isUntilNow}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      isUntilNow: e.target.checked,
                      MonthOut: e.target.checked ? '' : draft.MonthOut,
                      YearOut: e.target.checked ? '' : draft.YearOut,
                    })
                  }
                />
                <label htmlFor="isWorkExperienceUntilNow">Until Now</label>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Salary (Gross) </label>
            <input
              type="text"
              inputMode="numeric"
              className="form-control"
              id="txtSalary"
              placeholder="Rp. 000,000,000,000"
              value={draft.Salary}
              onChange={(e) => setDraft({ ...draft, Salary: e.target.value })}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Job Description </label>
            <textarea
              className="form-control"
              id="JobDescription"
              rows={10}
              cols={50}
              value={draft.JobDescription}
              onChange={(e) => setDraft({ ...draft, JobDescription: e.target.value })}
            />
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <div className="d-flex justify-content-end">
            <button
              type="button"
              id="btnAddWorkExperience"
              className="btn btn-secondary px-4"
              onClick={add}
            >
              Add Work Experience
            </button>
          </div>
        </div>
      </form>
      </Modal>
    </>
  )
}
